/**
 * Account runtime configuration — loads and merges per-account config with core defaults.
 *
 * New in Phase 1:
 *   loadFullAccountContext() — parallel load of profile + runtime config + VM config
 */

import { getFirestore, doc, getDoc, Timestamp } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { ACCOUNT_FIRESTORE_PREFIX, CORE_FIRESTORE_PREFIX } from './constants';
import type {
  AccountProfile,
  AccountRuntimeConfig,
  AccountVmConfig,
  FullAccountContext,
  AccountResolution,
} from './types';
import { resolveAccount } from './accountResolver';

// ─────────────────────────────────────────────────────────────────────────────
// Fallback defaults
// ─────────────────────────────────────────────────────────────────────────────

const FALLBACK_TIMESTAMP = Timestamp.fromDate(new Date(0));

/** Sensible defaults when core config is unavailable. */
const FALLBACK_CORE_DEFAULTS: Partial<AccountRuntimeConfig> = {
  personaName: 'Prest0n',
  welcomeMessage: "Hello! I'm Prest0n, your personal operations companion. How can I help you today?",
  allowedWorkflows: [],
  enabledCapabilities: [],
  brandingTone: 'professional',
  defaultLanguage: 'en',
  disclaimers: [],
  knowledgePolicy: 'account-plus-shared',
  coreReleasePinned: null,
  overlayVersion: null,
  migrationEnabled: false,
  voiceEnabled: false,
  defaultModel: 'sonnet',
  budgetCaps: {},
};

// ─────────────────────────────────────────────────────────────────────────────
// Individual loaders  (kept for backwards compat with existing callers)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Loads the account runtime config from accounts/{accountId}/config/runtime.
 * Returns null if the document does not exist or on error.
 */
export async function loadAccountRuntimeConfig(
  accountId: string
): Promise<AccountRuntimeConfig | null> {
  try {
    const db = getFirestore(getApp());
    const configRef = doc(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'config', 'runtime');
    const snapshot = await getDoc(configRef);
    if (!snapshot.exists()) return null;
    return normaliseRuntimeConfig(snapshot.data());
  } catch (err) {
    console.warn('[Prest0nRuntime] Failed to load account runtime config:', err);
    return null;
  }
}

/**
 * Merges an account-level config with core defaults loaded from Firestore.
 * Account-level values take precedence over core defaults.
 */
export async function mergeWithCoreDefaults(
  accountConfig: AccountRuntimeConfig
): Promise<AccountRuntimeConfig> {
  let coreDefaults: Partial<AccountRuntimeConfig> = FALLBACK_CORE_DEFAULTS;

  try {
    const db = getFirestore(getApp());
    const coreRef = doc(db, CORE_FIRESTORE_PREFIX, 'defaults');
    const snapshot = await getDoc(coreRef);
    if (snapshot.exists()) {
      coreDefaults = { ...FALLBACK_CORE_DEFAULTS, ...snapshot.data() };
    }
  } catch (err) {
    console.warn('[Prest0nRuntime] Failed to load core defaults, using fallbacks:', err);
  }

  return { ...coreDefaults, ...accountConfig } as AccountRuntimeConfig;
}

/**
 * Checks whether a specific capability is enabled for this account.
 */
export function isCapabilityEnabled(config: AccountRuntimeConfig, capability: string): boolean {
  return config.enabledCapabilities.includes(capability);
}

/**
 * Checks whether a specific workflow is allowed for this account.
 */
export function isWorkflowAllowed(config: AccountRuntimeConfig, workflowId: string): boolean {
  // Empty allowedWorkflows means all workflows are allowed
  if (config.allowedWorkflows.length === 0) return true;
  return config.allowedWorkflows.includes(workflowId);
}

// ─────────────────────────────────────────────────────────────────────────────
// New: Full account context loader
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Loads the complete account context in a single parallel fetch:
 *   - accounts/{accountId}/profile/main
 *   - accounts/{accountId}/config/runtime  (merged with core defaults)
 *   - accounts/{accountId}/vm/main
 *
 * Falls back gracefully if docs are partially populated.
 * Returns null if the profile doc does not exist (account not found).
 */
export async function loadFullAccountContext(
  accountId: string
): Promise<FullAccountContext | null> {
  try {
    const db = getFirestore(getApp());

    const [profileSnap, configSnap, vmSnap] = await Promise.all([
      getDoc(doc(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'profile', 'main')),
      getDoc(doc(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'config', 'runtime')),
      getDoc(doc(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'vm', 'main')),
    ]);

    if (!profileSnap.exists()) {
      console.warn('[Prest0nRuntime] loadFullAccountContext: no profile doc for', accountId);
      return null;
    }

    const profile = { accountId, ...profileSnap.data() } as AccountProfile;

    // Runtime config — normalise and merge with core defaults
    const rawConfig = configSnap.exists()
      ? normaliseRuntimeConfig(configSnap.data())
      : (FALLBACK_CORE_DEFAULTS as AccountRuntimeConfig);

    let coreDefaults: Partial<AccountRuntimeConfig> = FALLBACK_CORE_DEFAULTS;
    try {
      const coreRef = doc(db, CORE_FIRESTORE_PREFIX, 'defaults');
      const coreSnap = await getDoc(coreRef);
      if (coreSnap.exists()) {
        coreDefaults = { ...FALLBACK_CORE_DEFAULTS, ...coreSnap.data() };
      }
    } catch {
      // non-fatal
    }
    const runtimeConfig = { ...coreDefaults, ...rawConfig } as AccountRuntimeConfig;

    // VM config — optional
    const vmConfig: AccountVmConfig | null = vmSnap.exists()
      ? normaliseVmConfig(vmSnap.data())
      : null;

    // Build a lightweight AccountResolution from the profile data
    const resolution: AccountResolution = {
      accountId,
      userId: accountId, // Phase 1: userId = accountId for direct-UID accounts
      role: 'owner',
      firmName: profile.firmName || profile.displayName || 'Law Firm',
      tenantId: profile.tenantId ?? null,
      environment: profile.accountType === 'testing' ? 'testing' : 'production',
      isMigrated: runtimeConfig.migrationEnabled === true,
    };

    return { accountId, resolution, profile, runtimeConfig, vmConfig };
  } catch (err) {
    console.warn('[Prest0nRuntime] loadFullAccountContext failed:', err);
    return null;
  }
}

/**
 * Loads the full account context for the currently signed-in user by first
 * resolving the auth UID to an accountId via resolveAccount().
 *
 * Returns null for non-migrated users.
 */
export async function loadFullAccountContextForUser(
  authUid: string
): Promise<FullAccountContext | null> {
  const resolution = await resolveAccount(authUid);
  if (!resolution) return null;

  const ctx = await loadFullAccountContext(resolution.accountId);
  if (!ctx) return null;

  // Overwrite the resolution with the one that knows the actual userId + role
  return { ...ctx, resolution };
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal normalisation helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalises raw Firestore data to a typed AccountRuntimeConfig.
 * Handles legacy field shapes (e.g. disclaimers stored as a string instead of array).
 */
function normaliseRuntimeConfig(data: Record<string, unknown>): AccountRuntimeConfig {
  return {
    personaName: (data.personaName as string) ?? 'Prest0n',
    welcomeMessage: (data.welcomeMessage as string) ?? '',
    enabledCapabilities: asStringArray(data.enabledCapabilities),
    allowedWorkflows: asStringArray(data.allowedWorkflows),
    disclaimers: typeof data.disclaimers === 'string'
      ? (data.disclaimers ? [data.disclaimers] : [])
      : asStringArray(data.disclaimers),
    knowledgePolicy: (['account-only', 'account-plus-shared'].includes(data.knowledgePolicy as string)
      ? data.knowledgePolicy
      : 'account-plus-shared') as AccountRuntimeConfig['knowledgePolicy'],
    voiceEnabled: Boolean(data.voiceEnabled),
    defaultModel: (data.defaultModel as string) ?? 'sonnet',
    budgetCaps: (data.budgetCaps as AccountRuntimeConfig['budgetCaps']) ?? {},
    migrationEnabled: Boolean(data.migrationEnabled),
    coreReleasePinned: (data.coreReleasePinned as string | null) ?? null,
    overlayVersion: (data.overlayVersion as string | null) ?? null,
    brandingTone: (data.brandingTone as string) ?? undefined,
    practiceAreas: asOptionalStringArray(data.practiceAreas),
    defaultLanguage: (data.defaultLanguage as string) ?? undefined,
    updatedAt: (data.updatedAt as Timestamp) ?? FALLBACK_TIMESTAMP,
  };
}

/**
 * Normalises raw Firestore data to a typed AccountVmConfig.
 */
function normaliseVmConfig(data: Record<string, unknown>): AccountVmConfig {
  return {
    assigned: Boolean(data.assigned),
    assignmentMode: (['shared', 'dedicated'].includes(data.assignmentMode as string)
      ? data.assignmentMode
      : 'shared') as AccountVmConfig['assignmentMode'],
    vmName: (data.vmName as string | null) ?? null,
    zone: (data.zone as string | null) ?? null,
    machineType: (data.machineType as string | null) ?? null,
    environment: (['production', 'testing', 'development'].includes(data.environment as string)
      ? data.environment
      : 'production') as AccountVmConfig['environment'],
    provisioningStatus: (['PENDING', 'PROVISIONING', 'RUNNING', 'STOPPED', 'ERROR'].includes(
      data.provisioningStatus as string
    )
      ? data.provisioningStatus
      : 'STOPPED') as AccountVmConfig['provisioningStatus'],
    healthStatus: (data.healthStatus as string | null) ?? null,
    coreVersion: (data.coreVersion as string | null) ?? null,
    overlayVersion: (data.overlayVersion as string | null) ?? null,
    lastHeartbeatAt: (data.lastHeartbeatAt as Timestamp | null) ?? null,
    updatedAt: (data.updatedAt as Timestamp) ?? FALLBACK_TIMESTAMP,
    // Optional VM endpoint fields used by realtime desktop iframe routing.
    baseUrl: (data.baseUrl as string | null) ?? null,
    adapterUrl: (data.adapterUrl as string | null) ?? null,
    healthCheckUrl: (data.healthCheckUrl as string | null) ?? null,
    voiceWebhookUrl: (data.voiceWebhookUrl as string | null) ?? null,
    voiceHealthUrl: (data.voiceHealthUrl as string | null) ?? null,
    externalIp: (data.externalIp as string | null) ?? null,
    host: (data.host as string | null) ?? null,
    status: (data.status as string | null) ?? null,
    instanceName: (data.instanceName as string | null) ?? null,
  };
}

function asStringArray(val: unknown): string[] {
  if (Array.isArray(val)) return val.filter((v) => typeof v === 'string');
  return [];
}

function asOptionalStringArray(val: unknown): string[] | undefined {
  if (!val) return undefined;
  if (Array.isArray(val)) return val.filter((v) => typeof v === 'string');
  return undefined;
}
